<template>
  <v-dialog v-model="show" width="min(45%, 92vw)" persistent scrollable>
    <v-card>
      <v-card-title
        style="display: flex; flex-direction: column; align-items: start"
      >
        <span>{{ $t("btnEditTags") }}</span>
        <span
          v-if="sensorName"
          class="text-caption primary--text text--lighten-2"
        >
          {{ sensorName }}
        </span>
      </v-card-title>
      <v-card-text>
        <v-form ref="form">
          <div
            v-for="row in rows"
            v-bind:key="row.uid"
            style="display: flex; align-items: center"
          >
            <v-text-field
              v-model="row.key"
              v-bind:label="$t('tagKey')"
              v-bind:rules="keyRules"
              class="me-2"
            />
            <v-text-field
              v-model="row.value"
              v-bind:label="$t('tagValue')"
              v-bind:rules="valueRules"
              class="me-2"
            />
            <v-btn v-on:click="removeRow(row)" color="error" icon>
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>

          <div v-if="rows.length === 0">{{ $t("tagsNone") }}</div>
        </v-form>

        <v-btn v-on:click="addRow" text>
          <v-icon left>mdi-plus</v-icon>
          {{ $t("btnAddTag") }}
        </v-btn>

        <div class="text-caption grey--text mt-2">
          {{ $t("tagsValueHint") }}
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          v-on:click="closeDialog"
          v-bind:disabled="saving"
          color="error"
          text
        >
          {{ $t("btnCancel") }}
        </v-btn>
        <v-btn
          v-on:click="save"
          v-bind:loading="saving"
          v-bind:disabled="saving"
          color="primary"
          text
        >
          {{ $t("btnSave") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <ConfirmationDialog
      v-if="confirmDeleteAll"
      v-bind:callback="deleteAllTags"
      v-bind:content="$t('confirmDelete')"
      v-bind:title="$t('dialogDeleteConfirm')"
      v-on:cancel="confirmDeleteAll = false"
    />
  </v-dialog>
</template>

<script>
import ApiRequests from "../utils/requests";
import Messages from "../utils/messages";
import ConfirmationDialog from "./ConfirmationDialog";

/**
 * Dialog that edits the key/value tags of one asset or one measure.
 * Rows are edited locally and sent to the backend as a single batch on Save.
 */
export default {
  name: "TagsEditorDialog",

  components: { ConfirmationDialog },

  props: {
    assetId: { type: String, required: true },
    sensorName: { type: String, default: null },
    metaTagId: { type: String, default: null },
    tags: { type: Array, default: () => [] },
  },

  data() {
    return {
      show: true,
      saving: false,
      confirmDeleteAll: false,
      nextUid: 0,
      rows: [],
      keyRules: [
        (v) => !!v?.trim() || this.$t("required"),
        (v) => this.isKeyUnique(v) || this.$t("tagsDuplicateKey"),
      ],
      valueRules: [(v) => !!v?.trim() || this.$t("required")],
    };
  },

  created() {
    this.rows = this.tags.map((tag) => this.buildRow(tag.key, tag.value));
  },

  methods: {
    /** Builds a local row with a stable uid, without touching the tags prop. */
    buildRow(key, value) {
      return { uid: this.nextUid++, key, value };
    },
    addRow() {
      this.rows.push(this.buildRow("", ""));
    },
    removeRow(row) {
      this.rows = this.rows.filter((r) => r.uid !== row.uid);
    },
    /** Checks that a trimmed key is not repeated among the other rows. */
    isKeyUnique(key) {
      const trimmed = key?.trim();
      return this.rows.filter((r) => r.key?.trim() === trimmed).length <= 1;
    },
    closeDialog() {
      this.$emit("close");
    },
    /** Builds the attribute list sent to the backend, with trimmed key/value. */
    buildAttributes() {
      return this.rows.map((r) => ({
        key: r.key.trim(),
        value: r.value.trim(),
      }));
    },
    async save() {
      if (this.rows.length === 0) {
        if (this.metaTagId === null) {
          this.closeDialog();
        } else {
          this.confirmDeleteAll = true;
        }
        return;
      }

      if (!this.$refs.form.validate()) return;

      this.saving = true;

      try {
        const attributes = this.buildAttributes();

        if (this.metaTagId === null) {
          const res = await ApiRequests.createTags([
            {
              assetId: this.assetId,
              sensorName: this.sensorName,
              metadata: attributes,
            },
          ]);

          if (!res.data[0].success) {
            throw res.data[0].errorMessage;
          }
        } else {
          await ApiRequests.updateTag(this.metaTagId, attributes);
        }

        this.onSaveSuccess();
      } catch (err) {
        this.onSaveError(err);
      } finally {
        this.saving = false;
      }
    },
    /** Deletes the existing MetaTag after the user confirms removing all rows. */
    async deleteAllTags() {
      this.saving = true;

      try {
        await ApiRequests.deleteTag(this.metaTagId);
        this.onSaveSuccess();
      } catch (err) {
        this.onSaveError(err);
      } finally {
        this.saving = false;
      }
    },
    onSaveSuccess() {
      this.$bus.$emit(Messages.SUCCESS_MSG, this.$t("tagsSaveSuccess"));
      this.$emit("saved");
    },
    onSaveError(err) {
      if (process.env.NODE_ENV === "development") console.error(err);
      this.$bus.$emit(Messages.ERROR_MSG, {
        error: err?.response?.data?.error || err,
        description: this.$t("tagsSaveFailure"),
      });
    },
  },
};
</script>
